import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import { conversationSearchSchema, conversationsQueries } from '@/entities/conversation';
import { SupportInboxPage, type SupportInboxParams } from '@/pages/support-inbox';

/**
 * Support Inbox route — the URL is the single source of truth for the selected
 * folder, open chat, status tab, urgency sort and search.
 *   - `validateSearch` validates `{ folder, chat, status, urgent, search }` with
 *     the conversation entity's Zod schema (folder defaults to 'my', status to
 *     'ALL').
 *   - `loaderDeps` + `loader` prefetch the first page of the list NON-BLOCKING
 *     so the column shell renders immediately; `useConversations` surfaces state.
 *   - `beforeLoad` mirrors the orders auth guard.
 */

export const Route = createFileRoute('/inbox')({
  staticData: { permission: 'inbox.view' },
  beforeLoad: () => {
    // Auth guard: against the real API a token is required. In MSW dev mode the
    // mock backend authenticates everything, so the guard only bites with MSW off.
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: conversationSearchSchema,
  loaderDeps: ({ search }) => ({
    folder: search.folder,
    status: search.status,
    urgent: search.urgent,
    search: search.search,
  }),
  loader: ({ context, deps }) => {
    // Prefetch best-effort and NON-BLOCKING: kick off the first page of the list
    // but don't await it, so navigation never stalls on a slow/failing backend.
    void context.queryClient
      .prefetchInfiniteQuery(conversationsQueries.list(deps))
      .catch(() => undefined);
  },
  component: InboxRoute,
});

function InboxRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onParamsChange = (next: Partial<SupportInboxParams>) => {
    void navigate({
      search: (prev) => ({ ...prev, ...next }),
    });
  };

  return (
    <SupportInboxPage
      folder={search.folder}
      chatId={search.chat}
      status={search.status}
      urgent={search.urgent}
      search={search.search}
      onParamsChange={onParamsChange}
    />
  );
}
